import api from './api';
import { API_ENDPOINTS } from '../constants';

export interface CompanySettings {
  companyName: string;
  companyCode: string;
  totalBranches: number;
  totalDepartments: number;
  totalEmployees: number;
  inactiveEmployees: number;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  email?: string;
}

export interface AttendanceSettings {
  allowPunchFromApp: boolean;
  selfieAttendance: boolean;
  qrAttendance: boolean;
  gpsAttendance: boolean;
  markFromOfficeOnly: boolean;
  allowedRadius: number;
  requirePhoto: boolean;
  autoApproveOvertime: boolean;
  maxOvertimeHours: number;
  gracePeriodMinutes: number;
}

export interface SecuritySettings {
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    passwordExpiryDays: number;
  };
  sessionSettings: {
    sessionTimeoutMinutes: number;
    maxConcurrentSessions: number;
    requireReauthForSensitiveActions: boolean;
  };
  auditLogging: {
    enabled: boolean;
    retentionDays: number;
    logLevel: 'basic' | 'detailed' | 'comprehensive';
  };
}

export interface NotificationSettings {
  emailNotifications: {
    enabled: boolean;
    attendanceAlerts: boolean;
    leaveRequests: boolean;
    systemUpdates: boolean;
    securityAlerts: boolean;
  };
  smsNotifications: {
    enabled: boolean;
    attendanceAlerts: boolean;
    emergencyAlerts: boolean;
  };
  pushNotifications: {
    enabled: boolean;
    attendanceReminders: boolean;
    leaveApprovals: boolean;
    systemAnnouncements: boolean;
  };
}

export interface SystemInfo {
  version: string;
  lastUpdate: string;
  databaseSize: string;
  totalUsers: number;
  totalStores: number;
  totalClusters: number;
  systemUptime: string;
  serverStatus: 'healthy' | 'warning' | 'error';
}

export interface StorageCleanupSettings {
  photoRetentionDays: number;
  cleanupSchedule: string;
  photosCleanedUp: string[];
  photosRetained: string[];
  lastCleanupRun: string | null;
  nextScheduledRun: string;
}

export interface StorageCleanupStats {
  attendance: { processed: number; deleted: number; errors: number };
  temperatureCapture: { processed: number; deleted: number; errors: number };
  vehicleCapture: { processed: number; deleted: number; errors: number };
  securityLog: { processed: number; deleted: number; errors: number };
  totalS3Deletions: number;
}

export interface StorageCleanupResult {
  message: string;
  stats: StorageCleanupStats;
  triggeredBy: string;
  completedAt: string;
}

const settingsService = {
  /**
   * Get company settings
   */
  getCompanySettings: async (): Promise<CompanySettings> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: CompanySettings;
    }>(API_ENDPOINTS.SETTINGS.COMPANY);
    return response.data.data;
  },

  /**
   * Update company settings
   */
  updateCompanySettings: async (settings: Partial<CompanySettings>): Promise<CompanySettings> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: CompanySettings;
    }>(API_ENDPOINTS.SETTINGS.COMPANY, settings);
    return response.data.data;
  },

  /**
   * Get attendance settings
   */
  getAttendanceSettings: async (): Promise<AttendanceSettings> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: AttendanceSettings;
    }>(API_ENDPOINTS.SETTINGS.ATTENDANCE_MODES);
    return response.data.data;
  },

  /**
   * Update attendance settings
   */
  updateAttendanceSettings: async (settings: Partial<AttendanceSettings>): Promise<AttendanceSettings> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: AttendanceSettings;
    }>(API_ENDPOINTS.SETTINGS.ATTENDANCE_MODES, settings);
    return response.data.data;
  },

  /**
   * Get security settings
   */
  getSecuritySettings: async (): Promise<SecuritySettings> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: SecuritySettings;
    }>(API_ENDPOINTS.SETTINGS.SECURITY);
    return response.data.data;
  },

  /**
   * Update security settings
   */
  updateSecuritySettings: async (settings: Partial<SecuritySettings>): Promise<SecuritySettings> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: SecuritySettings;
    }>(API_ENDPOINTS.SETTINGS.SECURITY, settings);
    return response.data.data;
  },

  /**
   * Get notification settings
   */
  getNotificationSettings: async (): Promise<NotificationSettings> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: NotificationSettings;
    }>(API_ENDPOINTS.SETTINGS.NOTIFICATIONS);
    return response.data.data;
  },

  /**
   * Update notification settings
   */
  updateNotificationSettings: async (settings: Partial<NotificationSettings>): Promise<NotificationSettings> => {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: NotificationSettings;
    }>(API_ENDPOINTS.SETTINGS.NOTIFICATIONS, settings);
    return response.data.data;
  },

  /**
   * Get system information
   */
  getSystemInfo: async (): Promise<SystemInfo> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: SystemInfo;
    }>(API_ENDPOINTS.SETTINGS.SYSTEM_INFO);
    return response.data.data;
  },

  /**
   * Create data backup
   */
  createBackup: async (): Promise<{ backupId: string; downloadUrl: string }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: { backupId: string; downloadUrl: string };
    }>(API_ENDPOINTS.SETTINGS.BACKUP);
    return response.data.data;
  },

  /**
   * Get backup list
   */
  getBackups: async (): Promise<Array<{
    id: string;
    createdAt: string;
    size: string;
    type: 'full' | 'incremental';
    status: 'completed' | 'in_progress' | 'failed';
  }>> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: Array<{
        id: string;
        createdAt: string;
        size: string;
        type: 'full' | 'incremental';
        status: 'completed' | 'in_progress' | 'failed';
      }>;
    }>(API_ENDPOINTS.SETTINGS.BACKUP);
    return response.data.data;
  },

  /**
   * Download backup
   */
  downloadBackup: async (backupId: string): Promise<Blob> => {
    const response = await api.get(`${API_ENDPOINTS.SETTINGS.BACKUP}/${backupId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Test email configuration
   */
  testEmailConfiguration: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
    }>(`${API_ENDPOINTS.SETTINGS.NOTIFICATIONS}/test-email`);
    return response.data;
  },

  /**
   * Test SMS configuration
   */
  testSMSConfiguration: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
    }>(`${API_ENDPOINTS.SETTINGS.NOTIFICATIONS}/test-sms`);
    return response.data;
  },

  /**
   * Get storage cleanup settings
   */
  getStorageCleanupSettings: async (): Promise<StorageCleanupSettings> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: StorageCleanupSettings;
    }>(API_ENDPOINTS.SETTINGS.STORAGE_CLEANUP);
    return response.data.data;
  },

  /**
   * Manually trigger photo cleanup job
   */
  runStorageCleanup: async (): Promise<StorageCleanupResult> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: StorageCleanupResult;
    }>(API_ENDPOINTS.SETTINGS.STORAGE_CLEANUP_RUN);
    return response.data.data;
  },
};

export default settingsService;
